import React from "react"
import { Link } from "react-router-dom"
import { ArrowUpRight } from "lucide-react"

const services = [
  {
    number: "01",
    title: "Architecture",
    description:
      "From conceptual sketches to construction documentation — we deliver buildings that balance structural logic with spatial poetry. Residential, commercial, cultural, and mixed-use.",
    tags: ["Residential", "Commercial", "Cultural", "Mixed-Use"],
    image: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=600&q=80&fit=crop",
  },
  {
    number: "02",
    title: "Interior Design",
    description:
      "Spaces curated with intention. We craft interiors that reflect identity — layering material, light, and form to create environments that feel both composed and alive.",
    tags: ["Residential", "Hospitality", "Retail", "Corporate"],
    image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80&fit=crop",
  },
  {
    number: "03",
    title: "3D Visualization",
    description:
      "Photorealistic renders and cinematic walkthroughs that communicate architecture before a single wall is built. Marketing assets, planning submissions, and investor presentations.",
    tags: ["Renders", "Animations", "VR Tours", "Fly-Throughs"],
    image: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=600&q=80&fit=crop",
  },
  {
    number: "04",
    title: "Urban Planning",
    description:
      "Masterplanning and urban design that connects communities, honors context, and creates lasting civic value. We think at every scale — from the room to the city block.",
    tags: ["Masterplanning", "Landscape", "Feasibility", "Zoning"],
    image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&q=80&fit=crop",
  },
]

export default function ServicesGrid() {
  return (
    <section className="py-24 lg:py-32 bg-stone-900">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-4">
              <span className="w-6 h-px bg-amber-400" />
              What We Do
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl lg:text-6xl font-light text-stone-100 leading-tight">
              Full-spectrum<br />
              <span className="italic text-stone-400">design services</span>
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[11px] tracking-[0.2em] text-stone-400 hover:text-amber-400 transition-colors group self-start lg:self-auto"
          >
            ALL SERVICES <ArrowUpRight size={14} />
          </Link>
        </div>

        {/* Services List */}
        <div className="space-y-0 divide-y divide-stone-800/60">
          {services.map((service) => (
            <div
              key={service.number}
              className="group py-10 flex flex-col lg:flex-row gap-8 lg:gap-0 items-start cursor-pointer hover:bg-stone-800/20 transition-colors duration-300 -mx-6 lg:-mx-12 px-6 lg:px-12"
            >
              {/* Number */}
              <div className="w-16 shrink-0">
                <span className="font-['Space_Grotesk'] text-xs text-stone-600 tracking-widest">
                  {service.number}
                </span>
              </div>

              {/* Title */}
              <div className="lg:w-64 shrink-0">
                <h3 className="font-['Cormorant_Garamond'] text-3xl font-light text-stone-100 group-hover:text-amber-300 transition-colors">
                  {service.title}
                </h3>
              </div>

              {/* Description */}
              <div className="flex-1">
                <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed max-w-lg mb-5">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {service.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-['Space_Grotesk'] text-[10px] tracking-[0.15em] text-stone-500 border border-stone-700 px-3 py-1 group-hover:border-stone-600 group-hover:text-stone-400 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Image Reveal */}
              <div className="lg:ml-8 w-full lg:w-40 h-28 overflow-hidden shrink-0 opacity-0 lg:opacity-40 group-hover:lg:opacity-100 group-hover:lg:scale-100 transition-all duration-500">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Arrow */}
              <div className="hidden lg:flex items-center pl-6 self-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight size={20} className="text-amber-400" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
